/* eslint-disable no-underscore-dangle */
/**
 * Chuẩn bị request axios để ký — CommonJS, không import gì ngoài signer-core,
 * để chạy được cả trong webpack lẫn `node scripts/check-signing.js`.
 *
 * Vì sao interceptor phải tự làm phần việc của axios:
 * - axios nối baseURL + params vào URL SAU interceptor → ký URL thiếu query.
 * - axios JSON.stringify body SAU interceptor → hash object chứ không phải
 *   đúng byte gửi đi.
 * Nên ở đây dựng sẵn URL cuối (params = undefined) và body chuỗi.
 */
const core = require('./signer-core');

const ABSOLUTE = /^[a-z][a-z\d+\-.]*:\/\//i;

function hostOf(url) {
  // Trình duyệt coi `\` như `/` và bỏ khoảng trắng trong authority: `https://evil.com\@api/x`
  // thực ra gửi tới evil.com. Authority có ký tự lạ thì không tin là host API.
  const authority = /^[a-z][a-z\d+\-.]*:\/\/([^/?#]*)/i.exec(url || '');
  if (authority && /[\\\s]/.test(authority[1])) return null;
  const match = /^[a-z][a-z\d+\-.]*:\/\/(?:[^/?#@]*@)?([^/?#:]+)/i.exec(url || '');
  return match ? match[1].toLowerCase() : null;
}

// Đường dẫn của URL tuyệt đối: sau authority, trước `?` / `#`.
function pathOf(url) {
  const match = /^[a-z][a-z\d+\-.]*:\/\/[^/?#]*([^?#]*)/i.exec(url || '');
  return match ? match[1] : '';
}

// Server chỉ kiểm chữ ký dưới /api (SIGNATURE_SCOPE_PREFIX=/api/). Ký thêm cho /storage,
// /kanji… chỉ tạo header tuỳ biến → preflight CORS lên file tĩnh có thể không trả lời.
function isApiPath(url) {
  const path = pathOf(url);
  return path === '/api' || path.startsWith('/api/');
}

/**
 * URL mà người gọi đã truyền vào, trước khi prepareRequest đổi thành URL tuyệt đối.
 * Dùng để quyết định theo ý người gọi (vd. interceptor refresh có thêm /pure-route hay không).
 */
function callerUrl(config) {
  return config.__urlBeforeSigning !== undefined ? config.__urlBeforeSigning : config.url;
}

function combine(base, relative) {
  if (!relative) return base;
  return `${base.replace(/\/+$/, '')}/${relative.replace(/^\/+/, '')}`;
}

// FormData phải được loại trước isPlainObject: một số môi trường (polyfill, React Native)
// trả '[object Object]' cho FormData, JSON.stringify sẽ biến nó thành '{}'.
function isPlainObject(value) {
  return (
    value !== null &&
    typeof value === 'object' &&
    Object.prototype.toString.call(value) === '[object Object]'
  );
}

function isFormData(value) {
  return typeof FormData !== 'undefined' && value instanceof FormData;
}

function isURLSearchParams(value) {
  return typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams;
}

function createSigning(options) {
  const clock = options.clock || core.createClock();
  const nonce = options.nonce || core.randomNonce;

  function prepareRequest(config) {
    const url = config.url || '';
    const full = ABSOLUTE.test(url) ? url : combine(config.baseURL || '', url);
    // Trình duyệt (WHATWG URL) bỏ tab/LF/CR khỏi URL trước khi gửi, còn signer-core thì mã hoá
    // chúng: không bỏ ở đây thì chuỗi đã ký khác chuỗi server nhận → lệch chữ ký.
    const built = options.buildURL(full, config.params, config.paramsSerializer);
    const finalUrl = built.replace(/[\t\n\r]/g, '');
    if (hostOf(finalUrl) !== options.apiHost || !isApiPath(finalUrl)) return config;

    const headers = Object.assign({}, config.headers);
    let { data } = config;
    if (isURLSearchParams(data)) {
      data = data.toString();
      headers['Content-Type'] = 'application/x-www-form-urlencoded;charset=utf-8';
    } else if (!isFormData(data) && (isPlainObject(data) || Array.isArray(data))) {
      data = JSON.stringify(data);
      headers['Content-Type'] = 'application/json;charset=utf-8';
    }

    const signed = core.signRequest({
      method: config.method || 'get',
      url: finalUrl,
      body: typeof data === 'string' ? data : null,
      multipart: isFormData(data),
      keyId: options.keyId,
      timestamp: clock.timestamp(),
      nonce: nonce(),
      sign: options.sign
    });

    return Object.assign({}, config, {
      url: finalUrl,
      // Url gốc của người gọi (giữ giá trị đầu tiên khi request được gửi lại): url giờ đã tuyệt đối.
      __urlBeforeSigning: callerUrl(config),
      baseURL: undefined,
      params: undefined,
      data,
      headers: Object.assign(headers, signed)
    });
  }

  function observe(response) {
    if (response && response.headers && response.headers.date) clock.observe(response.headers.date);
  }

  function onResponse(response) {
    observe(response);
    return response;
  }

  /**
   * 401 kèm x-signature-reject là side-server chặn chữ ký, KHÔNG phải hết phiên:
   * đánh dấu để luồng refresh token bỏ qua. `expired` (máy sai giờ) thì đã chỉnh
   * giờ theo Date của chính response này — gửi lại đúng một lần, nonce mới.
   */
  function onError(error, retry) {
    const response = error && error.response;
    if (response) observe(response);
    const reason =
      response && response.status === 401 && response.headers
        ? response.headers[core.REJECT_HEADER]
        : null;
    if (!reason) return Promise.reject(error);

    Object.assign(error, { isSignatureReject: true, signatureReject: reason });
    const { config } = error;
    if (reason === 'expired' && config && !config.__signatureRetried) {
      config.__signatureRetried = true;
      return retry(config);
    }
    return Promise.reject(error);
  }

  return { clock, prepareRequest, onResponse, onError };
}

module.exports = { createSigning, hostOf, callerUrl };
